/**
 * RunResultBars — the brand's tall-vertical-bar run strip.
 *
 * A row of status bars (pass = green, flaky = yellow, running = violet, fail =
 * hatched burnt orange so it differs from pass without color), the active run
 * popped taller with an inset ring.
 * Presentational and run-kind agnostic: callers map their runs to
 * {@link RunBarSegment}s and supply navigation. No card/border of its own — it
 * sits inline in a header.
 *
 * Shared by the workflow run detail (`RunHealthStrip`) and the exploratory run
 * detail so the two stay visually identical.
 *
 * @module ui/components/RunResultBars
 */

import React from 'react';
import { runBarColor, Tooltip, TooltipTrigger, useTheme, failHatchStyle } from '@chatsdet/ui';

export type RunBarResult = 'pass' | 'flaky' | 'running' | 'fail' | 'skip' | 'idle';

export type RunBarSegment = {
  id: string;
  result: RunBarResult;
  /** Whether this is the run currently being viewed (taller + inset ring). */
  isActive: boolean;
  /** Accessible name for the bar's button. */
  ariaLabel: string;
  /** Hover tooltip content. */
  tooltip: React.ReactNode;
  /** Draw a small dot under the bar (e.g. a failure that repeats an earlier one). */
  marked?: boolean;
};

type RunResultBarsProps = {
  /** Bars in display order: oldest on the left, newest on the right. */
  segments: RunBarSegment[];
  /** Invoked when a non-active bar is clicked. Omit for a read-only strip. */
  onSelect?: (id: string) => void;
  /**
   * Pad the strip on the LEFT with idle bars up to this many slots (the brand's
   * fixed-width health strip). Overflowing segments are trimmed to the most
   * recent `slots`. Omit to render exactly the segments given, no padding.
   */
  slots?: number;
  /** Rendered after the bars (e.g. a health icon + pass-rate %). */
  trailing?: React.ReactNode;
  className?: string;
};

export const RunResultBars: React.FC<RunResultBarsProps> = ({
  segments,
  onSelect,
  slots,
  trailing,
  className,
}) => {
  const { config } = useTheme();
  const barStyle = (result: RunBarResult) => ({
    backgroundColor: runBarColor(result, config.theme),
    ...failHatchStyle(result === 'fail'),
  });
  const shown = slots !== undefined ? segments.slice(-slots) : segments;
  const emptySlots = slots !== undefined ? Math.max(0, slots - shown.length) : 0;

  return (
    <div className={`flex items-center gap-2.5${className ? ` ${className}` : ''}`}>
      <span className="inline-flex items-end gap-[2px]">
        {Array.from({ length: emptySlots }).map((_, i) => (
          <span
            key={`empty-${i}`}
            className="inline-block h-[14px] w-[7px] flex-shrink-0"
            style={barStyle('idle')}
          />
        ))}
        {shown.map((seg) => {
          const interactive = !!onSelect && !seg.isActive;
          return (
            <TooltipTrigger key={seg.id} delay={0} offset={8} placement="top">
              <button
                type="button"
                aria-label={seg.ariaLabel}
                aria-current={seg.isActive ? 'true' : undefined}
                // aria-disabled (not native `disabled`) keeps hover/focus events
                // flowing so the tooltip opens on bars that do nothing on click.
                aria-disabled={!interactive ? 'true' : undefined}
                onClick={interactive ? () => onSelect?.(seg.id) : undefined}
                // Active run pops taller with an inset ring (inset so it never
                // bleeds over neighbours); others lift slightly on hover.
                className={`relative inline-block flex-shrink-0 transition-all ${
                  seg.isActive
                    ? 'h-[18px] w-[7px] cursor-default'
                    : interactive
                      ? 'h-[14px] w-[7px] cursor-pointer hover:h-[16px]'
                      : 'h-[14px] w-[7px] cursor-default'
                }`}
                style={{
                  ...barStyle(seg.result),
                  boxShadow: seg.isActive ? 'inset 0 0 0 1.5px rgba(17,24,39,0.85)' : undefined,
                }}
              >
                {/* Absolutely positioned under the bar so marked bars never shift layout. */}
                {seg.marked ? (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-1/2 top-full mt-[2px] h-[3px] w-[3px] -translate-x-1/2 rounded-full bg-muted-foreground"
                  />
                ) : null}
              </button>
              <Tooltip variant="light" className="px-2 py-1 text-xs">
                {seg.tooltip}
              </Tooltip>
            </TooltipTrigger>
          );
        })}
      </span>
      {trailing}
    </div>
  );
};
